import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import API from '../../../services/API'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import AmbuNetLogo from '../../../assets/images/ambunetLogo.png'
import Stack from '@mui/material/Stack'
import StatusCard from '../../../components/Cards/StatusCard'
import FolderSharedIcon from '@mui/icons-material/FolderShared'
import { SiteModeContext } from '../../../components/mainFrame/MainFrame'
import { UserContext } from '../../../App'
import IncidentModule from './Incident/IncidentModule'
import EPCRDashboard from './EPCRDashboard'
import EPCRHeader from './EPCRHeader'
import moment from 'moment'
import DemographicsModule from './Demographics/Demographics'
import HistoryModule from './History/History'
import Assess from './Assess/Assess'
import PMHx from './PMHx/PMHx'
import Vitals from './Vitals/Vitals'
import Treatment from './Treatment/Treatment'
import Plan from './Plan/Plan'
import Sharing from './Sharing/Sharing'
import { Alert, AlertTitle, Typography } from '@mui/material'
import SidebarEPCR from './SidebarEPCR'
import TempReview from './Review/TempReview'
import Safeguarding from './Safeguarding/Safeguarding'
import EPCRCard from '../../../components/Cards/EPCRCard'
import DeletePopup from './DeletePopup'
import _ from 'lodash'
import Attachments from './Attachments/Attachments'
import FastTrackForm from './FastTrack/FastTrackForm'
import WelfareForm from './Welfare/WelfareForm'
import FormTypeDialog from './FormTypeDialog'
import LiveModule from './liveCapture/LiveModule'
import { isFullLayoutMode } from './formTypes'
import { getArrestPhase } from './liveCapture/engine'
import { isArrestInProgress } from './liveCapture/liveUi'

export const EPCRContext = createContext({})

const EPCR = ({ existingRecord, setExistingRecord, onExit }) => {
  const navigate = useNavigate()
  const isEmbedded = !!onExit
  const [selectedEPCRModule, setSelectedEPCRModule] = useState('Incident')
  const {
    siteMode,
    setSiteMode,
    setSidebarState,
    setEpcrPracticeMode,
    epcrPracticeMode,
  } = useContext(SiteModeContext)
  const { user } = useContext(UserContext)
  // Subcontractors have no employee doc; use their User ID so the default
  // staffInvolved entry matches the selector options (which list
  // subcontractors by User ID) and resolves consistently server-side
  const currentClinician =
    user.employee ??
    (user.subcontractor ? { ...user.subcontractor, _id: user._id } : null)
  const [uploadState, setUploadState] = useState('done')
  const [downloadState, setDownloadState] = useState('done')
  const [formValues, setFormValues] = useState({
    mode: 'ePCR',
    incident: {
      incidentDate: moment(),
      incidentNumber: `${moment().format('DDMMYY')}-`,
      incidentType: 'Event',
      staffInvolved: [
        {
          _id: currentClinician?._id,
          clinicalRole: currentClinician?.clinicalRole,
          userAccount: {
            firstName: user.firstName,
            lastName: user.lastName,
          },
        },
      ].filter((clinician) => !!clinician._id),
      lockedStaffInvolved: currentClinician?._id ? [currentClinician._id] : [],
      incidentVehicles: [],
      callTime: new Date(),
      patientSideTime: new Date(),
    },
    demographics: {
      consentsToSMS: true,
    },
    review: {
      status: 'Draft',
    },
  })
  const [formID, setFormID] = useState(null)
  const [fetchError, setFetchError] = useState(false)
  const [isDraftDirty, setIsDraftDirty] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [openDelete, setOpenDelete] = useState(false)
  // Ask for the record type up front on brand-new records
  const [formTypeDialogOpen, setFormTypeDialogOpen] = useState(!existingRecord)

  useEffect(() => {
    setSidebarState((prev) => ({ ...prev, mobileOpen: !isEmbedded }))

    return () => {
      setSidebarState((prev) => ({ ...prev, mobileOpen: false }))
    }
  }, [isEmbedded, setSidebarState])

  // 1. Check for an existing Record
  useEffect(() => {
    setDownloadState('pending')
    if (existingRecord) {
      API.get('/epcr/' + existingRecord).then((res) => {
        if (res.status === 200) {
          setFormValues(res.data)
          setFormID(res.data._id)
          setDownloadState('done')
          // A record with an arrest still running opens on the arrest
          // console; otherwise critical care records open on Live
          if (
            isFullLayoutMode(res.data?.mode) &&
            isArrestInProgress(res.data, getArrestPhase)
          ) {
            setSelectedEPCRModule('Arrest')
          } else if (res.data?.mode === 'Critical Care') {
            setSelectedEPCRModule('Live')
          }
        } else {
          setFetchError(true)
        }
      })
    }
    return () => {
      setFetchError(false)
    }
  }, [existingRecord])

  // Latest formValues, for saves to tell whether the record changed while
  // their request was in flight (rapid live-console taps). If it did, the
  // draft stays dirty so autosave sends the newer values too.
  const formValuesRef = useRef(formValues)
  formValuesRef.current = formValues

  // One key per new-record session, sent on every first-save attempt. If the
  // server created the record but the response was lost (poor signal, client
  // timeout), the retry is deduped onto that record instead of minting a new
  // number and orphaning the first one.
  const clientDraftIdRef = useRef(null)
  // The in-flight first save, so autosave and submit can't both POST at once
  const firstSaveRef = useRef(null)
  // A deduped retry gets back the record the earlier attempt created, without
  // this attempt's values written to it — so after any retry, follow up with a
  // PUT of the latest values.
  const firstSaveAttemptsRef = useRef(0)

  // 2. Save First Instance
  const postFirstSave = useCallback(async () => {
    setIsSaving(true)
    firstSaveAttemptsRef.current += 1
    const isRetry = firstSaveAttemptsRef.current > 1
    try {
      const sent = formValues
      const res = await API.post(
        '/ePCR',
        { ...formValues, clientDraftId: clientDraftIdRef.current },
        { skipGlobalErrorHandler: true },
      )

      if (res.status === 200) {
        const changedSince = isRetry || formValuesRef.current !== sent
        setFormID(res.data.formID)
        setFormValues((prev) => ({
          ...prev,
          _id: res.data.formID,
          epcrNumber: res.data.epcrNumber,
        }))
        setUploadState(changedSince ? 'pending' : 'done')
        setIsDraftDirty(changedSince)
        return true
      }

      setUploadState('error')
      return false
    } catch (e) {
      setUploadState('error')
      console.log(e)
      return false
    } finally {
      setIsSaving(false)
    }
  }, [formValues])

  const handleFirstSave = useCallback(() => {
    if (firstSaveRef.current) return firstSaveRef.current
    if (!formValues.incident.staffInvolved?.length) {
      console.warn('Not saving EPCR: no staff attached')
      setUploadState('error')
      return Promise.resolve(false)
    }
    if (!clientDraftIdRef.current) {
      clientDraftIdRef.current =
        typeof crypto !== 'undefined' && crypto?.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}_${Math.random().toString(16).slice(2)}`
    }
    firstSaveRef.current = postFirstSave().finally(() => {
      firstSaveRef.current = null
    })
    return firstSaveRef.current
  }, [postFirstSave, formValues])

  // 3. Save Updates
  const handleSubsequentSaves = useCallback(async () => {
    setIsSaving(true)
    const sent = formValues
    const newValues = _.cloneDeep(formValues)
    delete newValues.review.completedBy
    delete newValues.review.currentOwner
    delete newValues.review.shareCodes
    // Server-owned authorship fields - the server ignores them anyway, but
    // stripping avoids echoing populated objects back in the payload
    delete newValues.review.completedByRecord
    delete newValues.review.submittedBy
    delete newValues.review.submittedByRecord
    delete newValues.review.ownershipHistory
    delete newValues.incident.staffInvolvedRecords
    newValues.incident.staffInvolved = (
      formValues.incident.staffInvolved || []
    ).map((x) => x?._id || x?.userAccount?._id || x)

    try {
      const res = await API.put('/ePCR/' + formID, newValues, {
        skipGlobalErrorHandler: true,
      })

      if (res.status === 200) {
        const changedSince = formValuesRef.current !== sent
        setUploadState(changedSince ? 'pending' : 'done')
        setIsDraftDirty(changedSince)
        return true
      }

      setUploadState('error')
      return false
    } catch (e) {
      setUploadState('error')
      console.log(e)
      return false
    } finally {
      setIsSaving(false)
    }
  }, [formID, formValues])

  const saveDraft = useCallback(async () => {
    if (epcrPracticeMode) return true

    if (!formID) {
      return handleFirstSave()
    }

    return handleSubsequentSaves()
  }, [epcrPracticeMode, formID, handleFirstSave, handleSubsequentSaves])

  useEffect(() => {
    if (!isDraftDirty || isSaving) {
      return
    }

    const id = setTimeout(() => {
      if (!epcrPracticeMode) {
        saveDraft()
      }
    }, 4000)

    // Cleanup must clear the pending timer on every re-run and on unmount —
    // a survivor can fire a stale Draft PUT after the record was Submitted.
    return () => clearTimeout(id)
  }, [isDraftDirty, formValues, isSaving, saveDraft, epcrPracticeMode])

  const handleChange = ({ moduleName, field, value }) => {
    setUploadState((prev) => (prev === 'pending' ? prev : 'pending'))
    setFormValues((prev) => ({
      ...prev,
      [moduleName]: {
        ...(prev[moduleName] || {}),
        [field]: value,
      },
    }))
    setIsDraftDirty(true)
  }

  if (
    formValues.review.currentOwner &&
    user._id !== formValues.review.currentOwner._id
  ) {
    return (
      <EPCRCard>
        <Alert severity="error">
          <AlertTitle>
            This EPCR is currently being edited by{' '}
            {formValues.review.currentOwner.firstName}{' '}
            {formValues.review.currentOwner.lastName}
          </AlertTitle>
          <Stack>
            <Typography>
              To gain access to this record, you'll need to contact them.
            </Typography>
            <Button
              onClick={() => {
                setFetchError(false)
                setSelectedEPCRModule('')
                setSiteMode('normal')
                setEpcrPracticeMode(false)
                navigate('/epcr')
              }}
              variant="contained"
            >
              Go Back
            </Button>
          </Stack>
        </Alert>
      </EPCRCard>
    )
  }

  return (
    <EPCRContext.Provider
      value={{
        formValues,
        handleChange,
        uploadState,
        setUploadState,
        downloadState,
        setDownloadState,
        existingRecord,
        setExistingRecord,
        selectedEPCRModule,
        setSelectedEPCRModule,
        formID,
        setFormValues,
        openDelete,
        setOpenDelete,
        isSaving,
        setIsSaving,
        handleFirstSave,
        saveDraft,
        isDraftDirty,
        setIsDraftDirty,
        setFormTypeDialogOpen,
        onExit,
      }}
    >
      <Box display="flex" maxWidth="100%" height="100vh" overflow="hidden">
        {isFullLayoutMode(formValues.mode) && (
          <SidebarEPCR
            selectedEPCRModule={selectedEPCRModule}
            setSelectedEPCRModule={setSelectedEPCRModule}
            onExit={onExit}
          />
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            gap: 1,
            backgroundColor: '#F1F5F9',
            padding: { xs: 1, md: 1.5 },
          }}
        >
          <EPCRHeader
            epcrMode={formValues?.mode ?? 'ePCR'}
            epcrNumber={formValues?.epcrNumber}
            ptDetails={`${
              formValues?.demographics?.lastName
                ? formValues?.demographics?.lastName + ','
                : ''
            } ${
              formValues?.demographics?.firstName
                ? formValues?.demographics?.firstName[0]
                : ''
            } ${
              formValues?.demographics?.dob
                ? (() => {
                    const dob = moment(formValues.demographics.dob)
                    const now = moment()
                    const years = now.diff(dob, 'years')
                    const months = now.diff(
                      dob.clone().add(years, 'years'),
                      'months',
                    )

                    if (years >= 12) return `(${years} years)`
                    if (years === 0) return `(${months} months)`
                    return `(${years} years, ${months} months)`
                  })()
                : ''
            }`}
          />
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            flex={1}
            gap={2}
          >
            <Box
              flex={1}
              sx={{
                flexDirection: 'column',
                justifyContent: 'space-between',
                flex: 1,
                height: '100%',
              }}
            >
              {fetchError ? (
                <Alert severity="error">
                  <AlertTitle>
                    Unable to fetch Data. Please try again later.
                  </AlertTitle>
                  <Button
                    onClick={() => {
                      setFetchError(false)
                      setSelectedEPCRModule('')
                      if (onExit) {
                        onExit()
                      } else {
                        setSiteMode('normal')
                        setEpcrPracticeMode(false)
                        navigate('/epcr')
                      }
                    }}
                    variant="contained"
                    color="info"
                  >
                    Go Back
                  </Button>
                </Alert>
              ) : formValues.mode === 'Fast Track' ? (
                <FastTrackForm />
              ) : formValues.mode === 'Welfare' ? (
                <WelfareForm />
              ) : selectedEPCRModule === 'Live' ||
                selectedEPCRModule === 'Arrest' ? (
                <LiveModule
                  key={selectedEPCRModule}
                  initialView={selectedEPCRModule}
                />
              ) : selectedEPCRModule === 'Demographics' ? (
                <DemographicsModule />
              ) : selectedEPCRModule === 'History' ? (
                <HistoryModule />
              ) : selectedEPCRModule === 'Assess' ? (
                <Assess handleChange={handleChange} formValues={formValues} />
              ) : selectedEPCRModule === 'PMHx' ? (
                <PMHx />
              ) : selectedEPCRModule === 'Vitals' ? (
                <Vitals />
              ) : selectedEPCRModule === 'Treatment' ? (
                <Treatment />
              ) : selectedEPCRModule === 'Safeguarding' ? (
                <Safeguarding />
              ) : selectedEPCRModule === 'Plan' ? (
                <Plan />
              ) : selectedEPCRModule === 'Sharing' ? (
                <Sharing />
              ) : selectedEPCRModule === 'Review' ? (
                <TempReview setSelectedEPCRModule={setSelectedEPCRModule} />
              ) : selectedEPCRModule === 'Attachments' ? (
                <Attachments />
              ) : (
                <IncidentModule />
              )}
            </Box>
            <DeletePopup
              open={openDelete}
              onClose={() => setOpenDelete(false)}
              epcrID={formID}
              onDeleted={onExit}
            />
            <FormTypeDialog
              open={formTypeDialogOpen}
              onClose={() => setFormTypeDialogOpen(false)}
            />

            <Box
              sx={{
                fontFamily: 'moonlander',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                color: '#2A3948',
                opacity: 0.45,
                fontSize: 13,
                paddingY: 1.5,
              }}
            >
              <Box>AmbuNet &#174;</Box>
              <Box component="img" width={40} src={AmbuNetLogo} />
            </Box>
          </Box>
        </Box>
      </Box>
    </EPCRContext.Provider>
  )
}

export default EPCR
